import { Building2, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isJwtPlatformSuperadminSession } from '../../shared/access/access';
import { resolveFirstAllowedRoute } from '../../shared/access/route-resolver';
import { useAuth } from '../../shared/auth/use-auth';
import { useOrgDisplayName } from '../../shared/hooks/use-org-display-name';
import {
  staffTopbarEffectiveOrganizationId,
  staffTopbarOrgContextPlaqueReplacesCenterPicker,
  staffTopbarOrgContextPlaqueShowClearAction
} from './staff-topbar-org-context';

/** Как в v2-шапке админки: контекст организации + выход из имперсонации справа, не в центре с лукапом. */
export function StaffImpersonationBadge() {
  const navigate = useNavigate();
  const { me, impersonateOrgId, setImpersonateOrgId, refreshMe } = useAuth();
  const [exiting, setExiting] = useState(false);

  const effectiveOrgId = staffTopbarEffectiveOrganizationId(me, impersonateOrgId);
  const { displayName: orgLabel, title: orgTitle } = useOrgDisplayName(effectiveOrgId);

  if (!staffTopbarOrgContextPlaqueReplacesCenterPicker(me, impersonateOrgId)) return null;

  async function exitImpersonation(): Promise<void> {
    setExiting(true);
    try {
      await setImpersonateOrgId(null);
      const m = await refreshMe(
        isJwtPlatformSuperadminSession(me) ? { superadminClearEffectiveOrg: true } : undefined
      );
      navigate(resolveFirstAllowedRoute(m?.capabilities ?? null, m), { replace: true });
    } finally {
      setExiting(false);
    }
  }

  return (
    <div
      className="inline-flex max-w-[min(300px,46vw)] min-w-0 shrink items-stretch gap-2 rounded-md border border-[hsl(var(--ui-primary)/0.35)] bg-[hsl(var(--ui-primary)/0.09)] py-0.5 pl-2.5 pr-1 text-[hsl(var(--ui-foreground))]"
      role="status"
      aria-live="polite"
    >
      <Building2 className="size-4 shrink-0 self-center text-[hsl(var(--ui-primary))]" aria-hidden />
      <span className="flex min-w-0 flex-col justify-center leading-tight" title={orgTitle ?? orgLabel}>
        <span className="truncate text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ui-muted-foreground))]">
          Контекст организации
        </span>
        <span className="truncate text-sm font-semibold">{orgLabel}</span>
      </span>
      {staffTopbarOrgContextPlaqueShowClearAction(impersonateOrgId) ? (
        <button
          type="button"
          className="-my-0.5 mr-0 inline-flex size-8 shrink-0 items-center justify-center self-center rounded-md border-0 bg-transparent text-[hsl(var(--ui-muted-foreground))] transition-colors hover:bg-[hsl(var(--ui-foreground)/0.09)] hover:text-[hsl(var(--ui-foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))] disabled:cursor-not-allowed disabled:opacity-55"
          aria-label="Выйти из режима организации"
          disabled={exiting}
          onClick={() => void exitImpersonation()}
        >
          <X className="size-4" strokeWidth={2.25} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
